import { Component, Prop, Event, EventEmitter, Method, State, Element, h } from '@stencil/core';
import { Color, Size } from '../../interface';
import { Area, CallStatus, DataIn, Lift, MovingState } from './kone-elevators-interface'

@Component({
  tag: 'lar-kone-elevators',
  styleUrl: 'kone-elevators.scss',
  shadow: true
})
export class KoneElevators {
  @Element() el!: HTMLLarDebugElement; //

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'elevator1';
  /**
   * The color to use from your application's color palette.
   * Detrouble options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Disable quck actions
   */
  @Prop() disableQuickActions: boolean = false;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() lift?: Lift;
  @State() callableAreas: Area[] = [];
  @State() callStatus?: CallStatus;
  @State() loading: boolean = true;
  @State() connection: boolean = true;
  @State() connectionError?: string;

  private node!: HTMLLarNodeElement;

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.output.emit();
  }
  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Input Larva event message (see abow)
   */
  @Method()
  async input(data: DataIn) {
    this.loading = false;
    if (Array.isArray(data?.callableAreas)) {
      this.callableAreas = data.callableAreas;
    }
    if (typeof data.connection === 'boolean') {
      this.connection = data.connection;
    }
    this.callStatus = data.callStatus;
    this.lift = data.lift;
    this.connectionError = data.error;
  }

  callElevator(areaId: string) {
    this.loading = true;
    this.output.emit({
      action: 'lift-call',
      areaId,
    });
  }

  render() {
    const compnentProps = {
      callStatus: this.callStatus,
      callableAreas: this.callableAreas,
      loading: this.loading,
      color: this.colorInputs || this.color,
      onCallelevator: (ev:CustomEvent) => this.callElevator(ev.detail),
      connectionError: this.connectionError,
      connection: this.connection
    };
    let iconSmall: string | undefined;
    let iconSmallColor = this.colorIconSmall;
    if (this.connection === false) {
      iconSmall = 'warning';
    } else if (this.callStatus) {
      iconSmall = 'elevator-call';
      if (this.callStatus === CallStatus.SERVED) {
        iconSmall = 'dot-circle';
      } else if (this.lift?.movingState === MovingState.DOWN) {
        iconSmall = 'arrow-circle-down';
      } else if (this.lift?.movingState === MovingState.UP) {
        iconSmall = 'arrow-circle-up';
      }
    }
    //
    if (this.allowIndicationAutoColoring && iconSmall === 'warning') {
      iconSmallColor = 'warning';
    }
    let value = ' ';
    if (this.callStatus === CallStatus.TIMEOUT) {
      value = 'elevators.call_timeout_short';
    } else if (this.callStatus === CallStatus.CANCELLED) {
      value = 'elevators.call_canceled_short';
    } else if (this.callStatus === CallStatus.SERVED) {
      value = 'elevators.arrived_short';
    } else if (this.callStatus && this.lift?.currentArea) {
      value = `${this.lift?.currentArea?.name ?? ''}`;
    }
    return [
      <lar-node
        value={value}
        icon={this.icon}
        hideTitles={this.hideTitles}
        color={this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.colorModal}
        colorInputs={this.colorInputs}
        loading={this.loading}
        colorIconSmall={iconSmallColor}
        iconSmall={iconSmall}
        log={this.log}
        nodeSize={this.nodeSize}
        component="lar-kone-elevators-content"
        componentProps={compnentProps}
      >
        <slot />
      </lar-node>
    ];
  }
}
